import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useConnections } from '@/features/connections';
import { useWorkflows } from '@/features/workflows';
import type {
  TriggerProfileRunView,
  TriggerProfileView,
} from '@/lib/api/generated/model';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

import { useTriggerProfile } from '../hooks/useTriggerProfile';
import { useTriggerProfileRuns } from '../hooks/useTriggerProfileRuns';
import { useUpdateTriggerProfileSchedule } from '../hooks/useUpdateTriggerProfileSchedule';
import { SignalList } from './SignalList';
import {
  JiraTriggerConfigSchema,
  ScheduledTriggerConfigSchema,
  WorkspaceTaskChangeConfigDisplaySchema,
  scheduleHasCronExpressionWhenRequired,
  scheduleHasPositiveIntervalWhenRequired,
  scheduleHasTimezoneWhenRequired,
  withDefaultedMode,
} from '../types';
import type { TriggerActionWire } from '../types';
import { resolveStepName } from '../workflowSteps';
import { PendingRows } from '@/shared/components/PendingRows';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * One run's outcome, derived — never stored — from its own fields
 * (research.md D9; backend `data-model.md`'s run-history example:
 * `signals_created: 0` alongside `"matched": false` in `details`, which
 * this view does not read — the counts already carry the fact `details`
 * repeats).
 *
 * Four states, not three: `signals_actioned < signals_created` on an
 * otherwise-successful run (Spec 065's correlation-miss case — a signal
 * matched something real, e.g. a GitHub PR, but no related task was found
 * to act on) must read differently from both a genuine failure and a run
 * that found nothing at all. Collapsing it into either neighbor would
 * either hide a working-as-intended gap inside "Matched," or imply
 * something failed when nothing did (User Story 3, SC-003).
 */
function runOutcome(
  run: TriggerProfileRunView,
): 'failed' | 'no-signals' | 'found-not-actioned' | 'matched' {
  if (run.status === 'failed') return 'failed';
  if (run.signals_created === 0) return 'no-signals';
  if (run.signals_actioned < run.signals_created) return 'found-not-actioned';
  return 'matched';
}

const OUTCOME_LABEL: Record<ReturnType<typeof runOutcome>, string> = {
  failed: 'Failed',
  'no-signals': 'Did not match',
  'found-not-actioned': 'Found, not actioned',
  matched: 'Matched',
};

function RunRow({ run }: { run: TriggerProfileRunView }) {
  const outcome = runOutcome(run);

  return (
    <li className="border-border flex flex-col gap-1 rounded-lg border px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <Badge
          variant={outcome === 'failed' ? 'destructive' : 'secondary'}
          className="shrink-0"
        >
          {OUTCOME_LABEL[outcome]}
        </Badge>
        <span
          className="text-muted-foreground shrink-0 text-xs tabular-nums"
          title={formatExactTime(run.started_at)}
        >
          {formatRelativeTime(run.started_at)}
        </span>
      </div>
      {outcome !== 'no-signals' ? (
        <p className="text-muted-foreground text-xs tabular-nums">
          {run.signals_created} signal{run.signals_created === 1 ? '' : 's'}{' '}
          produced, {run.signals_actioned} actioned
        </p>
      ) : null}
      {/* Only the sanitized `run.error` string ever renders here — never
          `run.details` (a free-form diagnostic object that may carry raw
          backend context) and never a stack trace (research.md; FR-015). */}
      {run.error ? (
        <p className="text-destructive text-xs">{run.error}</p>
      ) : null}
    </li>
  );
}

/**
 * The action portion of a `workspace_task_change` config summary — identical
 * in event and schedule mode (Spec 082), so `ConfigSummary` below renders it
 * once regardless of which mode branch it's in, rather than duplicating this
 * block per mode. `fallbackWorkflow` is the watched workflow (event mode
 * only) to fall back on for a `continue_task` action's step name — schedule
 * mode has none, and `create_task` never needs it (it always names its own
 * `target_workflow_id`).
 */
function ActionSummary({
  action,
  workspaceId,
  fallbackWorkflow,
}: {
  action: TriggerActionWire;
  workspaceId: string;
  fallbackWorkflow:
    ReturnType<typeof useWorkflows>['workflows'][number] | undefined;
}) {
  const { workflows } = useWorkflows(workspaceId);
  const targetWorkflow =
    action.kind === 'create_task'
      ? workflows.find((wf) => wf.id === action.target_workflow_id)
      : fallbackWorkflow;

  return action.kind === 'create_task' ? (
    <>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Creates a task in</dt>
        <dd>
          {targetWorkflow?.name ?? action.target_workflow_id}:{' '}
          {resolveStepName(targetWorkflow, action.target_step_id)}
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Title template</dt>
        <dd className="font-mono">{action.title_template}</dd>
      </div>
      {action.description_template ? (
        <div className="flex flex-col gap-0.5">
          <dt className="text-muted-foreground text-xs">
            Description template
          </dt>
          <dd className="font-mono">{action.description_template}</dd>
        </div>
      ) : null}
    </>
  ) : (
    <>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">
          Applies only while the source task is on
        </dt>
        <dd>{resolveStepName(fallbackWorkflow, action.source_step_id)}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">
          Continues the source task, moving it to
        </dt>
        <dd>{resolveStepName(fallbackWorkflow, action.target_step_id)}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Comment template</dt>
        <dd className="font-mono">{action.comment_template}</dd>
      </div>
    </>
  );
}

/**
 * A `workspace_task_change` profile's stored configuration, branching on its
 * activation `mode` (Spec 082): event mode shows the watched transition
 * exactly as before this feature; schedule mode shows its cadence instead —
 * there is no watched transition to show. Parses with the permissive
 * read-path `WorkspaceTaskChangeConfigDisplaySchema` (not the create form's
 * narrowed schema) so a legacy profile whose action is still `continue_task`
 * displays correctly rather than falling into the parse-failure branch
 * (FR-011). `withDefaultedMode` treats a config with no `mode` key (every
 * profile stored before this feature) as event mode (FR-010).
 */
function ConfigSummary({
  profile,
  workspaceId,
}: {
  profile: TriggerProfileView;
  workspaceId: string;
}) {
  const { workflows } = useWorkflows(workspaceId);
  const parsed = WorkspaceTaskChangeConfigDisplaySchema.safeParse(
    withDefaultedMode(profile.config),
  );

  if (!parsed.success) {
    return (
      <p className="text-muted-foreground text-sm">
        Configuration in an unexpected shape. This profile may have been created
        by a different driver or an older version of this UI.
      </p>
    );
  }

  if (parsed.data.mode === 'schedule') {
    return (
      <dl className="flex flex-col gap-3 text-sm">
        <div className="flex flex-col gap-0.5">
          <dt className="text-muted-foreground text-xs">Schedule</dt>
          <dd>{scheduleSummaryLabel(profile)}</dd>
        </div>
        <ActionSummary
          action={parsed.data.action}
          workspaceId={workspaceId}
          fallbackWorkflow={undefined}
        />
      </dl>
    );
  }

  const { workflow_id, from_step_id, to_step_id, action } = parsed.data;
  const workflow = workflows.find((wf) => wf.id === workflow_id);

  return (
    <dl className="flex flex-col gap-3 text-sm">
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Watched transition</dt>
        <dd>
          {workflow?.name ?? workflow_id}:{' '}
          {resolveStepName(workflow, from_step_id)} →{' '}
          {resolveStepName(workflow, to_step_id)}
        </dd>
      </div>
      <ActionSummary
        action={action}
        workspaceId={workspaceId}
        fallbackWorkflow={workflow}
      />
    </dl>
  );
}

/** The same watched-transition summary shape, for a scheduled GitHub/GitLab
 *  profile's `config` (`ScheduledTriggerConfigSchema`) — connection,
 *  repositories, watched activity, and the action, mirroring
 *  `ConfigSummary`'s `<dl>` pattern above rather than inventing a new one. */

/** A concise cadence string — `"every 300s"` or `"cron 0 * * * * (UTC)"` —
 *  read directly off the profile's own fields, same format
 *  `TriggerProfileList`'s row summary uses. */
function scheduleSummaryLabel(profile: TriggerProfileView): string {
  if (profile.schedule_type === 'cron') {
    return `cron ${profile.cron_expression ?? '—'} (${profile.timezone ?? '—'})`;
  }
  return `every ${profile.interval_seconds ?? '—'}s`;
}

function ScheduledConfigSummary({
  profile,
  workspaceId,
}: {
  profile: TriggerProfileView;
  workspaceId: string;
}) {
  const { connections } = useConnections(workspaceId);
  const parsed = ScheduledTriggerConfigSchema.safeParse(profile.config);

  if (!parsed.success) {
    return (
      <p className="text-muted-foreground text-sm">
        Configuration in an unexpected shape. This profile may have been created
        by a different driver or an older version of this UI.
      </p>
    );
  }

  const connection = (connections ?? []).find(
    (c) => c.id === profile.connection_id,
  );
  const { repositories, watch, action } = parsed.data;

  return (
    <dl className="flex flex-col gap-3 text-sm">
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Connection</dt>
        <dd>
          {connection
            ? `${connection.name} (${connection.type === 'github' ? 'GitHub' : 'GitLab'})`
            : (profile.connection_id ?? 'None')}
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Schedule</dt>
        <dd>{scheduleSummaryLabel(profile)}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Repositories</dt>
        <dd className="font-mono">{repositories.join(', ')}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Watched activity</dt>
        <dd>{watch && watch.length > 0 ? watch.join(', ') : 'Everything'}</dd>
      </div>
      {action.kind === 'create_task' ? (
        <>
          <div className="flex flex-col gap-0.5">
            <dt className="text-muted-foreground text-xs">Title template</dt>
            <dd className="font-mono">{action.title_template}</dd>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-0.5">
          <dt className="text-muted-foreground text-xs">Comment template</dt>
          <dd className="font-mono">{action.comment_template}</dd>
        </div>
      )}
    </dl>
  );
}

/** Same shape as `ScheduledConfigSummary` above, for a scheduled Jira
 *  profile's `config` (`JiraTriggerConfigSchema`, spec 074 US7) — project/
 *  issue-type/status/labels filter instead of repositories/watch. */
function JiraConfigSummary({
  profile,
  workspaceId,
}: {
  profile: TriggerProfileView;
  workspaceId: string;
}) {
  const { connections } = useConnections(workspaceId);
  const parsed = JiraTriggerConfigSchema.safeParse(profile.config);

  if (!parsed.success) {
    return (
      <p className="text-muted-foreground text-sm">
        Configuration in an unexpected shape. This profile may have been created
        by a different driver or an older version of this UI.
      </p>
    );
  }

  const connection = (connections ?? []).find(
    (c) => c.id === profile.connection_id,
  );
  const { project, issue_type, status, labels, action } = parsed.data;

  return (
    <dl className="flex flex-col gap-3 text-sm">
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Connection</dt>
        <dd>
          {connection ? connection.name : (profile.connection_id ?? 'None')}
        </dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Schedule</dt>
        <dd>{scheduleSummaryLabel(profile)}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Project</dt>
        <dd className="font-mono">{project}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Issue type</dt>
        <dd>{issue_type ?? 'Any'}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Status</dt>
        <dd>{status ?? 'Any'}</dd>
      </div>
      <div className="flex flex-col gap-0.5">
        <dt className="text-muted-foreground text-xs">Labels</dt>
        <dd>{labels && labels.length > 0 ? labels.join(', ') : 'Any'}</dd>
      </div>
      {action.kind === 'create_task' ? (
        <div className="flex flex-col gap-0.5">
          <dt className="text-muted-foreground text-xs">Title template</dt>
          <dd className="font-mono">{action.title_template}</dd>
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          <dt className="text-muted-foreground text-xs">Comment template</dt>
          <dd className="font-mono">{action.comment_template}</dd>
        </div>
      )}
    </dl>
  );
}

/**
 * Displays a scheduled profile's current cadence, and — the only editable
 * field a scheduled profile has besides `enabled` (FR-010) — lets a member
 * change it. Editing is inline, not a separate sheet: the only two fields
 * involved are already this small.
 */
function ScheduleEditor({
  workspaceId,
  profile,
}: {
  workspaceId: string;
  profile: TriggerProfileView;
}) {
  const update = useUpdateTriggerProfileSchedule(workspaceId);
  const [editing, setEditing] = useState(false);
  const [scheduleType, setScheduleType] = useState<'interval' | 'cron'>(
    profile.schedule_type === 'cron' ? 'cron' : 'interval',
  );
  const [intervalSeconds, setIntervalSeconds] = useState<number | null>(
    profile.interval_seconds,
  );
  const [cronExpression, setCronExpression] = useState<string | null>(
    profile.cron_expression,
  );
  const [timezone, setTimezone] = useState<string | null>(profile.timezone);

  // Reading a trigger is open to anyone who may read the workspace (spec 189).
  const canWork = useCanWork();

  function startEditing() {
    setScheduleType(profile.schedule_type === 'cron' ? 'cron' : 'interval');
    setIntervalSeconds(profile.interval_seconds);
    setCronExpression(profile.cron_expression);
    setTimezone(profile.timezone);
    update.reset();
    setEditing(true);
  }

  const draft = { scheduleType, intervalSeconds, cronExpression, timezone };
  const intervalInvalid = !scheduleHasPositiveIntervalWhenRequired(draft);
  const cronExpressionInvalid = !scheduleHasCronExpressionWhenRequired(draft);
  const timezoneInvalid = !scheduleHasTimezoneWhenRequired(draft);
  const canSave =
    !intervalInvalid && !cronExpressionInvalid && !timezoneInvalid;

  function save() {
    if (!canSave) return;
    update.mutate(
      {
        triggerProfileId: profile.id,
        schedule: {
          schedule_type: scheduleType,
          interval_seconds:
            scheduleType === 'interval' ? intervalSeconds : null,
          cron_expression: scheduleType === 'cron' ? cronExpression : null,
          timezone: scheduleType === 'cron' ? timezone : null,
        },
      },
      { onSuccess: () => setEditing(false) },
    );
  }

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm">
          {profile.schedule_type === 'cron'
            ? `${profile.cron_expression ?? '—'} (${profile.timezone ?? '—'})`
            : `Every ${profile.interval_seconds ?? '—'}s`}
        </p>
        {canWork ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={startEditing}
          >
            Edit schedule
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-4">
        {(['interval', 'cron'] as const).map((type) => (
          <label key={type} className="flex items-center gap-1.5 text-sm">
            <input
              type="radio"
              name="schedule-editor-type"
              checked={scheduleType === type}
              disabled={update.isPending}
              onChange={() => {
                setScheduleType(type);
              }}
              className="accent-primary"
            />
            {type === 'interval' ? 'Interval' : 'Cron'}
          </label>
        ))}
      </div>

      {scheduleType === 'cron' ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-0.5">
              <Label>Cron expression</Label>
              <RequiredMark />
            </div>
            <Input
              value={cronExpression ?? ''}
              disabled={update.isPending}
              placeholder="0 * * * *"
              className="font-mono"
              onChange={(event) => {
                const next = event.target.value;
                setCronExpression(next.trim() === '' ? null : next);
              }}
            />
            {cronExpressionInvalid ? (
              <p className="text-destructive text-xs">
                A cron expression is required.
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-0.5">
              <Label>Timezone</Label>
              <RequiredMark />
            </div>
            <Input
              value={timezone ?? ''}
              disabled={update.isPending}
              placeholder="UTC"
              onChange={(event) => {
                const next = event.target.value;
                setTimezone(next.trim() === '' ? null : next);
              }}
            />
            {timezoneInvalid ? (
              <p className="text-destructive text-xs">
                A timezone is required.
              </p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-0.5">
            <Label>Interval (seconds)</Label>
            <RequiredMark />
          </div>
          <Input
            type="number"
            min={1}
            value={intervalSeconds ?? ''}
            disabled={update.isPending}
            placeholder="300"
            onChange={(event) => {
              const raw = event.target.value;
              setIntervalSeconds(raw === '' ? null : Number(raw));
            }}
          />
          {intervalInvalid ? (
            <p className="text-destructive text-xs">
              A positive interval is required.
            </p>
          ) : null}
        </div>
      )}

      {update.isError ? (
        <p role="alert" className="text-destructive text-sm">
          The schedule could not be updated.
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={update.isPending}
          onClick={() => {
            setEditing(false);
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={update.isPending || !canSave}
          onClick={save}
        >
          {update.isPending ? 'Saving…' : 'Save schedule'}
        </Button>
      </div>
    </div>
  );
}

/**
 * One trigger profile's full stored configuration and its run history —
 * User Story 3. Opened from a `TriggerProfileList` row click (T024).
 *
 * A scheduled (`github`/`gitlab`) profile additionally shows its cadence,
 * editable via `ScheduleEditor` (FR-010) — everything else here (run
 * history, config summary shape, sheet chrome) is unchanged from Spec 060.
 *
 * Run-level only: shows each run's outcome, `signals_created`/
 * `signals_actioned`, and its `error` when failed — no per-signal
 * drill-down and no call to the `signals` endpoint (spec Clarifications;
 * research.md D5/D6). The backend's own signal-history endpoint is
 * documented as a debugging aid, not the attribution mechanism (that's
 * User Story 4, on the task itself) — so it has no place in this view.
 */
export function TriggerProfileDetailSheet({
  workspaceId,
  triggerProfileId,
  open,
  onCancel,
}: {
  workspaceId: string;
  triggerProfileId: string;
  open: boolean;
  onCancel: () => void;
}) {
  const {
    profile,
    isPending: profilePending,
    loadFailed: profileFailed,
  } = useTriggerProfile(workspaceId, triggerProfileId);
  const {
    runs,
    isPending: runsPending,
    loadFailed: runsFailed,
  } = useTriggerProfileRuns(workspaceId, triggerProfileId);

  const isScheduled =
    profile?.type === 'github' ||
    profile?.type === 'gitlab' ||
    profile?.type === 'jira';

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <SheetContent className="sm:max-w-xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {profile?.name ?? 'Trigger'}
            {profile?.deleted_at ? (
              <Badge variant="outline">(deleted)</Badge>
            ) : profile ? (
              <Badge variant={profile.enabled ? 'secondary' : 'outline'}>
                {profile.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            ) : null}
          </SheetTitle>
          <SheetDescription>
            {profile?.description ?? 'Configuration and run history.'}
          </SheetDescription>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">Configuration</h3>
            {profilePending ? (
              <PendingRows label="Loading configuration" rows={4} />
            ) : profileFailed || !profile ? (
              <p className="text-muted-foreground text-sm">
                This trigger profile could not be loaded.
              </p>
            ) : profile.type === 'jira' ? (
              <JiraConfigSummary profile={profile} workspaceId={workspaceId} />
            ) : isScheduled ? (
              <ScheduledConfigSummary
                profile={profile}
                workspaceId={workspaceId}
              />
            ) : (
              <ConfigSummary profile={profile} workspaceId={workspaceId} />
            )}
          </div>

          {isScheduled && profile ? (
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-medium">Schedule</h3>
              <ScheduleEditor workspaceId={workspaceId} profile={profile} />
            </div>
          ) : null}

          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">Run history</h3>
            {runsPending ? (
              <PendingRows label="Loading run history" />
            ) : runsFailed ? (
              <p className="text-muted-foreground text-sm">
                The run history could not be loaded.
              </p>
            ) : !runs || runs.length === 0 ? (
              // "Never run" — distinct from a run history that exists but
              // shows only non-matching entries (rendered below instead).
              <p className="text-muted-foreground text-sm">
                This trigger profile has never been evaluated.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {runs.map((run) => (
                  <RunRow key={run.id} run={run} />
                ))}
              </ul>
            )}
          </div>

          {/*
            Beside the run history, never inside it (spec 090). A run is what
            fired; a signal is what arrived, and a reader who cannot tell those
            apart cannot tell a broken integration from a quiet one — which is the
            complaint this section answers.

            Its own read, so a signals failure leaves the configuration and the
            runs above it intact (FR-010).
          */}
          <SignalList
            workspaceId={workspaceId}
            triggerProfileId={triggerProfileId}
          />
        </SheetBody>

        <SheetFooter>
          <SheetClose render={<Button type="button" variant="outline" />}>
            Close
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
